import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import InsertCommentOutlinedIcon from "@mui/icons-material/InsertCommentOutlined";

import {
  Loader,
  ButtonV1,
  TextField,
  StatusChip,
  PaginationAction,
  QuickFilter,
} from "components";
import { STATUS_CHIP, success, error } from "constants";
import { useToaster } from "hooks";
import { moneyFormat, formatDateTime_v2 } from "utils";
import { getCall_v2, postCall_v2 } from "services";

import {
  PENDING_REQUESTS_HEADERS,
  QUICK_FILTERS,
  ROWS_PER_PAGE,
  ACTION_BUTTONS_URL,
} from "./constants";
import ApproveRejectModal from "./components/ApproveRejectModal";

const PendingRequests = () => {
  const theme = useTheme();
  const triggerToaster = useToaster();

  const [pendingRequestsList, setPendingRequestsList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPageCount] = useState(0);
  const [selectedQuickFilter, setSelectedQuickFilter] = useState("All");
  const [isFetching, setIsFetching] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchedOrderNumber, setSearchedOrderNumber] = useState(null);
  const [modalConfig, setModalConfig] = useState({
    open: false,
    data: null,
    actionType: "approve",
  });

  const fetchPendingRequestsApi = async ({
    status,
    ordNumber = null,
    page = 0,
    size = ROWS_PER_PAGE,
  }) => {
    try {
      const params = {
        status,
        page,
        size,
        ordNumber,
      };

      return await getCall_v2(`${ACTION_BUTTONS_URL}/history`, params);
    } catch (err) {
      triggerToaster(
        err?.data?.errorMessage || "Unable to fetch pending requests",
        error,
      );
    }
  };

  const approvePaymentInstrumentApi = async ({ requestId, comments = "" }) => {
    return await postCall_v2(`${ACTION_BUTTONS_URL}/${requestId}/approve`, {
      remark: comments,
    });
  };

  const rejectPaymentInstrumentApi = async ({ requestId, comments = "" }) => {
    return await postCall_v2(`${ACTION_BUTTONS_URL}/${requestId}/reject`, {
      remark: comments,
    });
  };

  const cancelPaymentInstrumentApi = async ({ requestId, comments = "" }) => {
    return await postCall_v2(`${ACTION_BUTTONS_URL}/${requestId}/cancel`, {
      remark: comments,
    });
  };

  const loadPendingRequests = async (
    currentPage = page,
    filter = selectedQuickFilter,
    orderNum = searchedOrderNumber,
  ) => {
    try {
      setIsFetching(true);
      const statusParam = QUICK_FILTERS[filter];
      const orderNumberParam = orderNum?.trim() ? orderNum.trim() : null;

      const res = await fetchPendingRequestsApi({
        status: statusParam,
        ordNumber: orderNumberParam,
        page: currentPage,
        size: ROWS_PER_PAGE,
      });

      if (res?.data?.items) {
        setPendingRequestsList(res.data.items);
        setTotalCount(res.data?.total ?? res.data.items.length);
      }
    } catch (err) {
      triggerToaster(
        err?.data?.message || "Failed to fetch pending requests",
        error,
      );
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    loadPendingRequests(page, selectedQuickFilter, searchedOrderNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedQuickFilter]);

  const onPageChange = (_, pageNumber) => {
    setPageCount(pageNumber);
  };

  const handleQuickFilter = (filterKey) => {
    setSelectedQuickFilter(filterKey);
    setPageCount(0);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (!val.trim() && searchedOrderNumber) {
      setSearchedOrderNumber(null);
      setPageCount(0);
      loadPendingRequests(0, selectedQuickFilter, null);
    }
  };

  const handleSearch = () => {
    const orderNumber = searchInput.trim();
    if (!orderNumber) return;
    setSearchedOrderNumber(orderNumber);
    setPageCount(0);
    loadPendingRequests(0, selectedQuickFilter, orderNumber);
  };

  const handleOpenActionModal = (row, actionType) => {
    const normalizedRow = {
      ...row,
      requestedAt: formatDateTime_v2(row?.whenCreated),
      currentPaymentInstrument: row?.fromPaymentMode || "-",
      requestedPaymentInstrumentCategory: row?.toPaymentMode || "-",
    };

    setModalConfig({
      open: true,
      data: normalizedRow,
      actionType,
    });
  };

  const handleCloseModal = useCallback(() => {
    setModalConfig({
      open: false,
      data: null,
      actionType: "approve",
    });
  }, []);

  const handleSubmitModalAction = useCallback(
    async ({ requestId, ordNumber, comments }) => {
      try {
        const isApprove = modalConfig.actionType === "approve";
        const isCancel = modalConfig.actionType === "cancel";

        if (isApprove) {
          await approvePaymentInstrumentApi({ requestId, comments });
          triggerToaster(
            `Request for ${ordNumber} approved successfully`,
            success,
          );
        } else if (isCancel) {
          await cancelPaymentInstrumentApi({ requestId, comments });
          triggerToaster(
            `Request for ${ordNumber} cancelled successfully`,
            success,
          );
        } else {
          await rejectPaymentInstrumentApi({ requestId, comments });
          triggerToaster(
            `Request for ${ordNumber} rejected successfully`,
            success,
          );
        }
        handleCloseModal();
        loadPendingRequests(page, selectedQuickFilter, searchedOrderNumber);
      } catch (err) {
        triggerToaster(
          err?.data?.errorMessage || "Action failed, please try again",
          error,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalConfig.actionType, page, selectedQuickFilter, searchedOrderNumber],
  );

  const isApproved = (status) => status === "APPROVED";
  const isRejected = (status) => status === "REJECTED";
  const isPending = (status) => status === "PENDING_APPROVAL";
  const isCancelled = (status) => status === "CANCELLED";

  return (
    <Box p={4}>
      <Typography
        variant="h3"
        sx={{
          mb: 3,
          ml: 1,
        }}
      >
        All pending requests
      </Typography>

      <Box display="flex" alignItems="center" gap={2} mb={5}>
        <Box sx={{ maxWidth: 350, width: "100%" }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buyer’s Order ID"
            id="order-search"
            value={searchInput}
            onChange={handleSearchChange}
          />
        </Box>
        <ButtonV1
          title="Search"
          variant="contained"
          onClick={handleSearch}
          disabled={!searchInput.trim()}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <QuickFilter
          onQuickFilter={handleQuickFilter}
          state={selectedQuickFilter}
          filterData={QUICK_FILTERS}
        />
      </Box>

      <Box
        component={Paper}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {PENDING_REQUESTS_HEADERS.map((header) => (
                  <TableCell key={header} sx={{ whiteSpace: "nowrap" }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell
                    colSpan={PENDING_REQUESTS_HEADERS.length}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    <Loader sx={{ marginTop: 0 }} />
                  </TableCell>
                </TableRow>
              ) : !pendingRequestsList.length ? (
                <TableRow>
                  <TableCell
                    colSpan={PENDING_REQUESTS_HEADERS.length}
                    align="center"
                    sx={{ py: 6 }}
                  >
                    <Typography textAlign="center" color="textSecondary">
                      No data found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pendingRequestsList.map((row) => {
                  return (
                    <TableRow key={row.ordNumber}>
                      <TableCell>{row?.ordNumber || "-"}</TableCell>

                      <TableCell sx={{ textAlign: "center" }}>
                        {row?.customerName || "-"}
                      </TableCell>

                      <TableCell sx={{ fontSize: "13px" }}>
                        <Typography>
                          {formatDateTime_v2(row?.whenCreated)}
                        </Typography>
                      </TableCell>

                      <TableCell>{row?.requestedBy}</TableCell>

                      <TableCell sx={{ textAlign: "center" }}>
                        {row?.availableLimit != null
                          ? moneyFormat(row?.availableLimit)
                          : "-"}
                      </TableCell>

                      <TableCell sx={{ textAlign: "center" }}>
                        {row?.fromPaymentMode || "-"}
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ textAlign: "center" }}>
                          {row?.toPaymentMode || "-"}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {isApproved(row.status) && (
                          <StatusChip
                            label="Approved"
                            type={STATUS_CHIP?.success}
                          />
                        )}

                        {isRejected(row?.status) && (
                          <Box display="flex" alignItems="center">
                            <StatusChip
                              label="Rejected"
                              type={STATUS_CHIP?.error}
                            />
                            <Tooltip title={row?.reason || "-"} placement="top">
                              <IconButton
                                size="small"
                                sx={{
                                  ml: 3,
                                  p: 0.5,
                                }}
                              >
                                <InsertCommentOutlinedIcon
                                  sx={{
                                    fontSize: "18px",
                                    color: theme.palette.primary.main,
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}

                        {isCancelled(row?.status) && (
                          <Box display="flex" alignItems="center">
                            <StatusChip
                              label="Cancelled"
                              type={STATUS_CHIP?.warning}
                            />
                            <Tooltip title={row?.reason || "-"} placement="top">
                              <IconButton
                                size="small"
                                sx={{
                                  ml: 3,
                                  p: 0.5,
                                }}
                              >
                                <InsertCommentOutlinedIcon
                                  sx={{
                                    fontSize: "18px",
                                    color: theme.palette.primary.main,
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}

                        {isPending(row?.status) && (
                          <Box display="flex" alignItems="center" gap={3}>
                            <ButtonV1
                              title="Approve"
                              variant="contained"
                              size="small"
                              onClick={() =>
                                handleOpenActionModal(row, "approve")
                              }
                            />
                            <Typography
                              onClick={() =>
                                handleOpenActionModal(row, "reject")
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                              }}
                            >
                              Reject
                            </Typography>
                            <Typography
                              onClick={() =>
                                handleOpenActionModal(row, "cancel")
                              }
                              sx={{
                                color: theme.palette.error?.dark,
                                fontWeight: 600,
                              }}
                            >
                              Cancel
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalCount > 0 && (
          <TablePagination
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            rowsPerPage={ROWS_PER_PAGE}
            page={page}
            count={totalCount}
            onPageChange={onPageChange}
            sx={{ display: "flex", flexDirection: "column-reverse" }}
            ActionsComponent={PaginationAction}
          />
        )}
      </Box>

      <ApproveRejectModal
        open={modalConfig.open}
        onClose={handleCloseModal}
        data={modalConfig.data}
        actionType={modalConfig.actionType}
        onSubmit={handleSubmitModalAction}
      />
    </Box>
  );
};

export default PendingRequests;
